// React Query
import { useMutation } from "@tanstack/react-query";

// MUI
import { Stack } from "@mui/material";

// Hooks
import { useIsProducer } from "@Common/hooks/Positions";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// API
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import InfoSubTitle from "../../../common/InfoSubTitle";

// Types
import { IDrawModalities } from "@/Settings/types/Common/model";

type TDrawModalitiesProps = {
	locationId: string;
	position: string;
	drawModalities: IDrawModalities[] | undefined;
};

// Constants
const modalities = [
	{ label: "Flat (Draw)" },
	{ label: "Hourly (Draw)" },
	{ label: "Commission (Draw)" },
	{ label: "Commission Only (No Draw)" },
];

const DrawModalities = (props: TDrawModalitiesProps) => {
	const { locationId, position, drawModalities = [] } = props;

	/********************************************* MUTATIONS ************************************************/
	const { mutate, isPending } = useMutation({
		mutationFn: (input: { position: string; drawModality: string }) =>
			updateSystemSettings(locationId, { drawModalities: input }, "DRAW_MODALITIES"),
		onError: (error) => errorLogger(error),
	});

	/********************************************* HANDLERS ************************************************/
	const handleChangeModality = (value: { label: string }) => {
		const updatedSettings = {
			position,
			drawModality: value?.label,
		};
		mutate(updatedSettings);
	};

	const selectedModality = drawModalities?.find((element) => element.position === position);
	const isProducer = useIsProducer(position);

	const filteredModalities = modalities.filter((modality) => {
		// Just show this option if the position is a producer
		if (modality.label === "Commission Only (No Draw)") {
			return isProducer;
		}
		return true;
	});

	return (
		<Stack>
			<InfoSubTitle title="Draw Modality" className="!mb-[24px]" />
			<SmartSelector
				showBy="label"
				selectBy="label"
				options={filteredModalities}
				selected={selectedModality?.drawModality ? { label: selectedModality?.drawModality } : null}
				inputProps={{
					placeholder: "Select an Option",
				}}
				sx={{ width: "281px" }}
				disabled={isPending}
				error={!selectedModality?.drawModality}
				onChangeAction={(value) => handleChangeModality(value as { label: string })}
			/>
		</Stack>
	);
};

export default DrawModalities;
